import axios, { type AxiosRequestConfig } from "axios";
import { AxiosError, type AxiosResponse } from "axios";
import { Logger } from "tslog";
import type { ILogObj } from "tslog";
import * as Sentry from "@sentry/vue";
import { useAccountStore } from "@/stores/account";

declare module "axios" {
  interface AxiosRequestConfig {
    cancelPreviousRequest?: boolean;
    skipJWTRefresh?: boolean;
    skipRetry?: boolean;
    __retryCount?: number;
  }
}

const TRANSIENT_ERROR_CODES = ["ERR_NETWORK", "ECONNABORTED", "ETIMEDOUT"];
const IDEMPOTENT_METHODS = new Set(["get", "head", "options"]);
const RETRY_BASE = 500;
const MAX_RETRIES = 3;

export const GATEWAY_ERROR_STATUSES = [502, 503, 504];

const log = new Logger<ILogObj>({ name: "api" });

const api = axios.create({
  baseURL: "/api",
  xsrfCookieName: "csrftoken2",
  xsrfHeaderName: "X-CSRFToken",
});

const pendingRequests: Record<string, AbortController> = {};

api.interceptors.request.use(
  (config) => {
    if (config.cancelPreviousRequest && config.url && !config.signal) {
      const path = config.url.split("?")[0];
      const requestKey = `${config.method}:${path}`;
      pendingRequests[requestKey]?.abort();
      const controller = new AbortController();
      config.signal = controller.signal;
      pendingRequests[requestKey] = controller;
    }

    return config;
  },
  (error) => Promise.reject(error),
);

export class OfflineError extends AxiosError {
  public readonly originalError: AxiosError;

  constructor(originalError: AxiosError) {
    super(
      `It looks like you're offline. Please check your internet connection. ${originalError.message}`,
      originalError.code,
      originalError.config,
      originalError.request,
      originalError.response,
    );
    this.name = "OfflineError";
    this.originalError = originalError;
    this.stack = originalError.stack;
  }
}

function getFieldErrorMessages(data: unknown): string | null {
  if (!data || typeof data !== "object" || Array.isArray(data)) {
    return null;
  }
  const values = Object.values(data);
  const isFieldErrors =
    values.length > 0 &&
    values.every(
      (value) =>
        Array.isArray(value) && value.every((item) => typeof item === "string"),
    );
  return isFieldErrors ? values.flat().join(" ") : null;
}

function getErrorMessageFromResponse(
  response: AxiosResponse,
  request_config: AxiosRequestConfig | undefined,
) {
  if (response.data.detail) {
    if (typeof response.data.detail === "string") {
      return response.data.detail;
    }
    if (Array.isArray(response.data.detail)) {
      return response.data.detail
        .map((detail: any) => {
          const loc = detail.loc || [];
          const field = loc.length > 0 ? loc[loc.length - 1] : null;
          if (field) {
            return `${field}: ${detail.msg}`;
          }
          return detail.msg;
        })
        .join(" ");
    }
    return null;
  }

  if (response.data.error) {
    return response.data.error;
  }
  if (response.data.errors) {
    return response.data.errors.join(" ");
  }
  if (Array.isArray(response.data)) {
    return response.data.join(" ");
  }

  const requestMethod = request_config?.method?.toUpperCase() || "";
  const wasActionRequest = ["POST", "PATCH", "PUT", "DELETE"].includes(
    requestMethod,
  );

  if (response.status === 413) {
    return "Content too large";
  }
  if (response.status === 500) {
    if (wasActionRequest) {
      return "An unexpected error occurred. Please try again later (500)";
    }
    return "An unexpected error occurred (500)";
  }
  if (response.status === 502) {
    return "Some upstream server returned invalid data (502)";
  }
  if (response.status === 503 || response.status === 504) {
    if (wasActionRequest) {
      return `The server is temporarily unavailable. Please try again later (${response.status})`;
    }
    return `The server is temporarily unavailable (${response.status})`;
  }

  return getFieldErrorMessages(response.data) ?? JSON.stringify(response.data);
}

function isCanceledError(error: AxiosError) {
  return error.code === "ERR_CANCELED" || error.name === "CanceledError";
}

function isTransientNetworkError(error: AxiosError) {
  return !error.response && TRANSIENT_ERROR_CODES.includes(error.code ?? "");
}

export function isNetworkError(error: unknown): boolean {
  if (error instanceof OfflineError) {
    return true;
  }
  return error instanceof AxiosError && isTransientNetworkError(error);
}

function isRetryableRequest(config: AxiosRequestConfig) {
  const method = config.method?.toLowerCase() ?? "";
  const retryCount = config.__retryCount ?? 0;
  return (
    IDEMPOTENT_METHODS.has(method) &&
    retryCount < MAX_RETRIES &&
    !config.skipRetry
  );
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    if (isCanceledError(error)) {
      return Promise.reject(error);
    }

    const config = error.config;
    if (
      config &&
      isRetryableRequest(config) &&
      (isTransientNetworkError(error) ||
        GATEWAY_ERROR_STATUSES.includes(error.response?.status ?? 0))
    ) {
      const retryCount = (config.__retryCount ?? 0) + 1;
      config.__retryCount = retryCount;
      Sentry.addBreadcrumb({
        category: "http",
        message: "Retrying after network or gateway error",
        data: {
          url: config.url,
          method: config.method,
          status: error.response?.status,
          retryCount,
        },
      });
      await new Promise((resolve) =>
        setTimeout(resolve, RETRY_BASE * 2 ** (retryCount - 1)),
      );
      return api.request(config);
    }

    if (error.response?.status === 401) {
      await useAccountStore().logout();
    } else if (error.response?.data) {
      error.message =
        getErrorMessageFromResponse(error.response, error.config) ||
        error.message;
    } else if (error.code === "ERR_NETWORK" && !window.navigator.onLine) {
      error = new OfflineError(error);
    } else if (isTransientNetworkError(error)) {
      error.message = "Connection problem. Check network and try again.";
    } else {
      error.message = `The request could not be made: ${error.message}`;
    }

    log.error(error.message);
    return Promise.reject(error);
  },
);

export default api;
